// The single-player page's "Season, game by game" strip (Trevor 2026-10-02): every team game through the date, so a
// night he did not play reads as one. DNP = solid grey, labelled "DNP" on any run wide enough to hold the word;
// garbage time only = grey hatch with its own legend entry, so the two never look alike. Met / missed / no attempts
// keep the design's encoding. Nothing prints below 10px, so single grey cells rely on the legend (and a tooltip).
import { CSSProperties } from "react";
import { MET, shortDate } from "./kpiLib";
import type { StripState } from "./types";

const GREY = "#d9d6d2";
const HATCH = `repeating-linear-gradient(135deg, ${GREY} 0 2px, #fff 2px 4px)`;
const FILL: Record<StripState, CSSProperties> = {
  met: { background: MET.bg },
  miss: { background: "#fff", border: "1px solid #1a1a1a" },
  none: { background: "#fff", border: "1px dashed #cfccc8" },
  dnp: { background: GREY },
  garbage: { background: HATCH, border: `1px solid ${GREY}` },
};
const TIP: Record<StripState, string> = {
  met: "met",
  miss: "missed",
  none: "no attempts",
  dnp: "did not play",
  garbage: "garbage time only",
};
// A label needs about 24px of strip; the strip is ~3.9in (~375px) wide.
const STRIP_PX = 375;
const LABEL_PX = 24;

export default function GameStrip({
  cells,
  title,
  lastTxt,
}: {
  cells: { date: string; gameId: string; state: StripState }[];
  title: string;
  lastTxt: string;
}) {
  const n = cells.length;
  if (!n) return null;
  const gap = n > 40 ? 1 : 2;
  const runs: { start: number; len: number }[] = [];
  cells.forEach((c, i) => {
    if (c.state !== "dnp") return;
    const last = runs[runs.length - 1];
    if (last && last.start + last.len === i) last.len++;
    else runs.push({ start: i, len: 1 });
  });
  const labelled = runs.filter((r) => (r.len / n) * STRIP_PX >= LABEL_PX);
  const has = (s: StripState) => cells.some((c) => c.state === s);
  const kick: CSSProperties = {
    fontSize: 10,
    lineHeight: 1,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b6864",
    fontWeight: 700,
  };
  const swatch = (s: StripState): CSSProperties => ({
    display: "inline-block",
    width: 10,
    height: 10,
    boxSizing: "border-box",
    verticalAlign: -1,
    marginRight: 4,
    ...FILL[s],
  });
  return (
    <div style={{ marginTop: "auto", paddingTop: 10 }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", ...kick }}
      >
        <span>{title}</span>
        <span>
          {n} {n === 1 ? "game" : "games"}
        </span>
      </div>
      <div
        style={{
          position: "relative",
          display: "flex",
          gap,
          marginTop: 5,
          height: 13,
          alignItems: "stretch",
        }}
      >
        {cells.map((c, i) => (
          <i
            key={c.gameId}
            title={`${shortDate(c.date)} · ${TIP[c.state]}`}
            style={{
              display: "block",
              flex: 1,
              minWidth: 0,
              boxSizing: "border-box",
              ...FILL[c.state],
              ...(i === n - 1
                ? { outline: "2px solid #1a1a1a", outlineOffset: 1 }
                : {}),
            }}
          />
        ))}
        {labelled.map((r) => (
          <span
            key={r.start}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${(r.start / n) * 100}%`,
              width: `${(r.len / n) * 100}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "#6b6864",
              pointerEvents: "none",
            }}
          >
            DNP
          </span>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          lineHeight: 1,
          color: "#6b6864",
          marginTop: 4,
          gap: 10,
        }}
      >
        <span>{shortDate(cells[0]?.date)}</span>
        <span
          style={{
            display: "flex",
            gap: 10,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {has("dnp") && (
            <span>
              <i style={swatch("dnp")} />
              DNP
            </span>
          )}
          {has("garbage") && (
            <span>
              <i style={swatch("garbage")} />
              Garbage time only
            </span>
          )}
        </span>
        <span style={{ color: "#1a1a1a", fontWeight: 700 }}>{lastTxt}</span>
      </div>
    </div>
  );
}
